import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Grid,
    Divider,
    IconButton,
    CircularProgress,
    Box
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';
import { getAsStartAndEndTimeFormat, getAsLongDate, applyUkgUserTimeZoneTransformation } from '../../../../common-client2/src/services/ukgDateTime';
import './VtoPreviewModal.scss';



/**
 * Fetch VTO preview data from the API
 */
const fetchVtoPreview = async (managerSettingEntityId, partialStartDate, partialEndDate) => {
    const options = {
        headers: {
            Authorization: sessionStorage.getItem('authToken') ?? '',
        }
    };

    const url = `${process.env.REACT_APP_API_DOTNET}/api/v1/employee/VoluntaryTimeOff/GetVtoPreview?managerSettingEntityId=${managerSettingEntityId}&partialStartDate=${partialStartDate.toISOString()}&partialEndDate=${partialEndDate.toISOString()}`;
    const response = await axios.get(url, options);
    return response.data;
};

const VtoPreviewModal = ({
    open,
    onClose,
    managerSettingEntityId,
    partialStartDate,
    partialEndDate,
}) => {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'employee.partialVtoPreview' });
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            if (!managerSettingEntityId || !partialStartDate || !partialEndDate) {
                setError('Missing required data for preview');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            fetchVtoPreview(managerSettingEntityId, partialStartDate, partialEndDate)
                .then(data => {
                    setPreviewData(data);
                    setLoading(false);
                })
                .catch(err => {
                    console.error('Error fetching VTO preview:', err);
                    setError('Failed to load preview data: ' + (err.response?.data?.message || err.message));
                    setLoading(false);
                });
        }
    }, [open, managerSettingEntityId, partialStartDate, partialEndDate]);

    if (!open) {
        return null;
    }

    const renderShiftItem = (shift, index) => (
        <div key={index} className="vto-preview-shift-item">
            <Typography variant="body2">
                <strong>{shift.label || 'Shift'}</strong> - {getAsLongDate(shift.startDateTime)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {getAsStartAndEndTimeFormat(shift.startDateTime, shift.endDateTime)}
            </Typography>
        </div>
    );

    const renderPaycodeItem = (paycode, index) => {
        const duration = moment.duration(moment.utc(paycode.endDateTime).diff(moment.utc(paycode.startDateTime)));
        const hours = duration.asHours().toFixed(2);

        return (
            <div key={index} className="vto-preview-paycode-item">
                <Typography variant="body2">
                    <strong>{paycode.paycodeName}</strong> ({hours}h)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {getAsStartAndEndTimeFormat(paycode.startDateTime, paycode.endDateTime)}
                </Typography>
            </div>
        );
    };
    
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="vto-preview-modal"
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">{t('vtoPreviewTitle') || 'VTO Request Preview'}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </div>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                )}

                {error && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {!loading && !error && previewData && (
                    <Grid container spacing={3}>
                        {/* Original Schedule Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                {t('vtoPreviewOriginalSchedule') || 'Original Schedule'}
                            </Typography>
                            {previewData.affectedShifts && previewData.affectedShifts.length > 0 ? (
                                previewData.affectedShifts.map((shift, index) => renderShiftItem(shift, index))
                            ) : (
                            <Typography variant="body2" color="textSecondary">
                                {t('vtoPreviewNoShifts') || 'No shifts affected'}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Partial VTO Selection Section */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                            {t('vtoPreviewPartialSelection') || 'Partial VTO Selection'}
                        </Typography>
                        <div className="vto-preview-partial-selection">
                            <Typography variant="body2">
                                <strong>{t('vtoPreviewPeriod') || 'Period'}:</strong>{' '}
                                {getAsStartAndEndTimeFormat(moment.utc(partialStartDate).format('YYYY-MM-DD HH:mm:ss'), moment.utc(partialEndDate).format('YYYY-MM-DD HH:mm:ss'))}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {getAsLongDate(partialStartDate)}
                                {!moment.utc(partialStartDate).isSame(moment.utc(partialEndDate), 'day') &&
                                    ` - ${getAsLongDate(partialEndDate)}`}
                            </Typography>
                        </div>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                        {/* Resulting Shifts Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                {t('vtoPreviewResultingShifts') || 'Resulting Shifts (if any)'}
                            </Typography>
                            {(previewData.shiftsToKeepBefore && previewData.shiftsToKeepBefore.length > 0) ||
                             (previewData.shiftsToKeepAfter && previewData.shiftsToKeepAfter.length > 0) ? (
                                <>
                                    {previewData.shiftsToKeepBefore && previewData.shiftsToKeepBefore.map((shift, index) => renderShiftItem(shift, index))}
                                    {previewData.shiftsToKeepAfter && previewData.shiftsToKeepAfter.map((shift, index) => renderShiftItem(shift, index))}
                                </>
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    {t('vtoPreviewNoResultingShifts') || 'No shifts will remain after VTO approval'}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Divider />
                        </Grid>

                        {/* Resulting Paycodes Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                                {t('vtoPreviewResultingPaycodes') || 'Resulting Paycodes'}
                            </Typography>
                            {previewData.shiftsToPaycode && previewData.shiftsToPaycode.length > 0 ? (
                                previewData.shiftsToPaycode.map((paycode, index) => renderPaycodeItem(paycode, index))
                            ) : (
                                <Typography variant="body2" color="textSecondary">
                                    {t('vtoPreviewNoPaycodes') || 'No paycodes will be created'}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">
                    {t('close') || 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VtoPreviewModal;

